import { memo } from 'react'

import { OnlyFansMessageReleaseForm, ReleaseForm } from '@api'
import { format } from 'date-fns/format'

import { Checkbox } from '@/components/ui/checkbox'
import DescriptionIcon from '@/modules/chatting/icons/description.svg'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { classNames } from '@/utils/helpers'

import { TaggableItemActions } from '../TaggableItemActions'

interface Props {
  isSelected?: boolean
  style?: React.CSSProperties
  onChangeSelected?: (isSelected: boolean) => void
  onRemoveTag?: () => void
  releaseForm: ReleaseForm | OnlyFansMessageReleaseForm
}

export const ReleaseFormItem = memo(
  ({
    isSelected,
    style,
    onChangeSelected,
    onRemoveTag,
    releaseForm,
  }: Props) => {
    // status only in ReleaseForm type object passed from ReleaseFormTabContent
    const isFailed = 'status' in releaseForm && releaseForm.status === 'failed'
    const date = releaseForm.date ? format(releaseForm.date, 'MMM d, yyyy') : ''

    const additionalInfo = [isFailed && 'Failed', date].filter(
      (info): info is string => typeof info === 'string',
    )

    // it should be disabled only in ReleaseFormTabContent where selection is possible
    const isDisabled = !!onChangeSelected && isFailed

    return (
      <label
        htmlFor={String(releaseForm.id)}
        style={style}
        className={classNames(
          'hover:bg-primary-50 dark:hover:bg-secondary-600 cursor-pointer',
          { 'pointer-events-none cursor-not-allowed  opacity-70': isDisabled },
        )}
      >
        <div className="flex h-[82px] w-full items-center justify-between p-4">
          <div className="flex items-center justify-between gap-3">
            {!!onChangeSelected && (
              <Checkbox
                id={String(releaseForm.id)}
                onCheckedChange={onChangeSelected}
                checked={isSelected}
              />
            )}
            <div className="bg-secondary-50 dark:bg-secondary-600 flex h-[50px] w-[50px] items-center justify-center rounded-full">
              <DescriptionIcon />
            </div>
            <div className="space-y-0.5">
              <p className="font-medium leading-[26px]">
                {unescapeHtmlText(releaseForm.name)}
              </p>
              {additionalInfo.length > 0 && (
                <p className="text-secondary-300 text-sm leading-[22px]">
                  {additionalInfo.join(' • ')}
                </p>
              )}
            </div>
          </div>
          <TaggableItemActions
            taggable={releaseForm}
            onRemoveTag={onRemoveTag}
          />
        </div>
        <div className="ml-[78px] mr-4 border-b" />
      </label>
    )
  },
)

ReleaseFormItem.displayName = 'ReleaseFormItem'
